import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Offcanvas, OffcanvasHeader, OffcanvasBody, Row, Col, Collapse, FormGroup, Label, Input, Form, Button, Alert } from "reactstrap";
import PepUserActionDigDays from "./PepUserActionDigDays";
import { ACTION_PRIORITIES, ACTION_STATUS } from "../../../core/constant/pep/PepConstant";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import PepAttachDigDayModal from "./PepAttachDigDayModal";
import useUserRole from "../../../hooks/useUserRole";

const PepUserActionRowOffCanvas = ({ toggle, isVisible, selectedRow, onUpdateRow, onDataReload, currentPageIndex, isLoading }) => {
  const [isDigDaysPannelVisible, setIsDigDaysPannelVisible] = useState(true);
  const [isActionUpdatePannelVisible, setIsActionUpdatePannelVisible] = useState(true);
  const [progression, setProgression] = useState(0);
  const [status, setStatus] = useState();
  const [priority, setPriority] = useState();
  const [cancelActionClickedCount, setCancelActionClickedCount] = useState(0);

  const [isEditingComment, setIsEditingComment] = useState(false);
  const [commentValue, setCommentValue] = useState("");
  const [isSavingComment, setIsSavingComment] = useState(false);

  const { isAdmin, isManager } = useUserRole();
  const canEditComments = isAdmin || isManager;

  const handlePriorityChange = (event) => {
    setPriority(event.target.value);
  };

  const handleProgressionChange = (value) => {
    setStatus(value <= 100 ? ACTION_STATUS.find((status) => status.value === 1).value : ACTION_STATUS.find((status) => status.value === 0).value);
    setProgression(value);
  };

  useEffect(() => {
    setCancelActionClickedCount(0);
    setProgression(0);
    setIsEditingComment(false);
  }, [isVisible]);

  useEffect(() => {
    if (selectedRow) {
      setStatus(selectedRow.status);
      setPriority(ACTION_PRIORITIES.find((action) => action.value == selectedRow.priority).value);
      setProgression(Math.round(selectedRow.progress));
      setCommentValue(selectedRow.comments ?? "");
    }
  }, [selectedRow]);

  useEffect(() => {
    if (cancelActionClickedCount == 2) {
      const payload = [];
      payload.push({
        op: "replace",
        path: "/status",
        value: ACTION_STATUS.find((action) => action.value == 2).value,
      });
      onUpdateRow({ id: selectedRow.id, payload });
    }
  }, [cancelActionClickedCount]);

  const handleValuesUpdate = useCallback(() => {
    const payload = [];
    payload.push({ op: "replace", path: "/progress", value: progression });
    payload.push({ op: "replace", path: "/status", value: status });
    payload.push({ op: "replace", path: "/priority", value: parseInt(priority) });
    onUpdateRow({ id: selectedRow.id, payload });
  }, [selectedRow, status, priority, progression]);

  const handleSaveComment = useCallback(async () => {
    setIsSavingComment(true);
    const payload = [{ op: "replace", path: "/comments", value: commentValue }];
    await onUpdateRow({
      id: selectedRow.id,
      payload,
      successMessage: "Commentaire mis à jour avec succès !",
    });
    setIsSavingComment(false);
    setIsEditingComment(false);
  }, [selectedRow, commentValue, onUpdateRow]);

  const handleDetachDigDay = useCallback(
    (digDayIdToDetach) => {
      if (digDayIdToDetach && selectedRow) {
        const payload = [];
        payload.push({
          op: "replace",
          path: "/digDayIds",
          value: selectedRow?.digDayIds?.filter((dd) => dd !== digDayIdToDetach) ?? [],
        });
        onUpdateRow({ id: selectedRow.id, payload });
      }
    },
    [selectedRow]
  );

  const getCancelStatusButtonLabel = useMemo(() => {
    if (isLoading === true) return "Mise à jour en cours...";
    return cancelActionClickedCount < 1 ? "Annuler l'action" : "Oui, je veux annuler l'action";
  }, [isLoading, cancelActionClickedCount]);

  const isCancelButtonDisabled = useMemo(() => {
    return isLoading === true || (selectedRow?.digDayIds?.length ?? 0) > 0;
  }, [isLoading, selectedRow?.digDayIds]);

  return !selectedRow ? (
    <></>
  ) : (
    <Offcanvas toggle={toggle} direction="end" isOpen={isVisible}>
      <OffcanvasHeader toggle={toggle}>
        <span className="text-center">{`${selectedRow.userFullName} - ${selectedRow.title}`}</span>
      </OffcanvasHeader>
      <OffcanvasBody>
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>

          <div>
            <Row onClick={() => setIsDigDaysPannelVisible((prevState) => !prevState)} style={{ cursor: "pointer" }}>
              <Col md={4}>
                <strong className="text-center" style={{ color: "#2f528f" }}>
                  {`Dig days (${selectedRow?.digDayIds?.length ?? 0})`}
                </strong>
              </Col>
              <Col md={6}><hr /></Col>
              <Col md={2}>
                <i className={`bx bx-chevron-${isDigDaysPannelVisible ? "up" : "down"}`} />
              </Col>
            </Row>
            <Collapse isOpen={isDigDaysPannelVisible}>
              <div className="d-flex justify-content-end align-self-end mb-1">
                <PepAttachDigDayModal selectedPep={selectedRow} onDataReload={onDataReload} currentPageIndex={currentPageIndex} />
              </div>
              <PepUserActionDigDays userAction={selectedRow} handleOnDetach={handleDetachDigDay} />
              <hr />

              <div className="d-flex align-items-center justify-content-between pb-1">
                <span className="fs-6">
                  <strong>Commentaires :</strong>
                </span>
                {canEditComments && !isEditingComment && (
                  <Button
                    color="link"
                    size="sm"
                    className="p-0"
                    title="Modifier le commentaire"
                    onClick={() => setIsEditingComment(true)}
                  >
                    <i className="bx bx-edit fs-5 text-primary" />
                  </Button>
                )}
              </div>

              {isEditingComment ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <Input
                    type="textarea"
                    rows={4}
                    value={commentValue}
                    onChange={(e) => setCommentValue(e.target.value)}
                    placeholder="Ajouter un commentaire..."
                    disabled={isSavingComment}
                  />
                  <div className="d-flex gap-2 justify-content-end">
                    <Button
                      color="secondary"
                      size="sm"
                      outline
                      disabled={isSavingComment}
                      onClick={() => {
                        setCommentValue(selectedRow.comments ?? "");
                        setIsEditingComment(false);
                      }}
                    >
                      Annuler
                    </Button>
                    <Button
                      color="success"
                      size="sm"
                      disabled={isSavingComment}
                      onClick={handleSaveComment}
                    >
                      {isSavingComment
                        ? <><i className="bx bx-loader-alt bx-spin" /> Enregistrement...</>
                        : <><i className="bx bx-check" /> Enregistrer</>
                      }
                    </Button>
                  </div>
                </div>
              ) : (
                <span className="text-break">
                  {selectedRow?.comments || <em className="text-muted">Aucun commentaire</em>}
                </span>
              )}
              <div className="mb-2">
                <span className="fs-6 pb-2">
                  <strong> Instructions :</strong> <br />
                </span>
                {selectedRow?.instruction || <em className="text-center">Aucune instruction</em>}
              </div>
            </Collapse>
          </div>

          <div>
            <Row onClick={() => setIsActionUpdatePannelVisible((prevState) => !prevState)} style={{ cursor: "pointer" }}>
              <Col md={6}>
                <strong className="text-center" style={{ color: "#2f528f" }}>
                  Modifier l&apos;action
                </strong>
              </Col>
              <Col md={4}><hr /></Col>
              <Col md={2}>
                <i className={`bx bx-chevron-${isActionUpdatePannelVisible ? "up" : "down"}`} />
            </Col>
            </Row>
            <Collapse isOpen={isActionUpdatePannelVisible}>
              <div style={{ margin: "10px 24px 10px 24px", display: "flex", flexDirection: "column", gap: "24px" }}>
                <Form style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                  <FormGroup style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                    <div>
                      <Label for="progressionInpt">{`Progression: ${progression}%`}</Label>
                      <Slider
                        step={10}
                        marks={{ 10: 10, 20: 20, 30: 30, 40: 40, 50: 50, 60: 60, 70: 70, 80: 80, 90: 90, 100: 100 }}
                        onChange={(value) => handleProgressionChange(value)}
                        defaultValue={Math.round(selectedRow.progress)}
                        styles={{ rail: { border: "3px solid #2f528f" } }}
                      />
                    </div>
                  </FormGroup>
                  <FormGroup>
                    <div>
                      <Label for="prioritySelect">Priorité</Label>
                      <Input bsSize="md" type="select" onChange={handlePriorityChange}>
                        {ACTION_PRIORITIES.map((item, index) => (
                          <option key={index} selected={item.value === priority} value={item.value}>
                            {item.label}
                          </option>
                        ))}
                      </Input>
                    </div>
                  </FormGroup>
                </Form>
                <Button
                  color="success"
                  onClick={handleValuesUpdate}
                  style={{ width: "100%", backgroundColor: isLoading === true ? "#ccc" : "var(--main-color-green)" }}
                  disabled={isLoading === true}
                >
                  {isLoading === true ? "Mise à jour en cours..." : "Enregistrer les modifications"}
                </Button>
                <hr />
                {(selectedRow?.digDayIds?.length ?? 0) > 0 && (
                  <Alert color="info" className="mb-3">
                    <i className="bx bx-info-circle"></i> Cette action est liée à {selectedRow?.digDayIds?.length} Dig Day(s). Vous devez d&apos;abord les détacher avant de pouvoir annuler l&apos;action.
                  </Alert>
                )}
                <Button
                  color="secondary"
                  disabled={isCancelButtonDisabled}
                  onClick={() => setCancelActionClickedCount((prevState) => prevState + 1)}
                  style={{
                    width: "100%",
                    backgroundColor: isCancelButtonDisabled ? "#ccc" : "var(--main-color-red)",
                    color: "#fff"
                  }}
                  title={(selectedRow?.digDayIds?.length ?? 0) > 0 ? "Impossible d'annuler : au moins un Dig Day est relié" : ""}
                >
                  {getCancelStatusButtonLabel}
                </Button>
              </div>
            </Collapse>
          </div>

        </div>
      </OffcanvasBody>
    </Offcanvas>
  );
};

PepUserActionRowOffCanvas.propTypes = {
  toggle: PropTypes.func.isRequired,
  isVisible: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool,
  selectedRow: PropTypes.object,
  onUpdateRow: PropTypes.func,
  onDataReload: PropTypes.func,
  currentPageIndex: PropTypes.number,
};

export default PepUserActionRowOffCanvas;
